import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { distinctUntilChanged, filter, debounceTime } from "rxjs/operators";
import { Router } from "@angular/router";
import dayjs from "dayjs";
import { from } from "rxjs";

// Services
import { MsalService } from "@azure/msal-angular";
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import {
  DatabaseCacheService,
  Section,
} from "../../../data/cache/database-cache.service";
import { GetParameterService } from "../../../data/record/get-parameter.service";
import { UserDataTemplateListComponent } from "../user-data-template-list/user-data-template-list.component";
import { DuplicateCheckService } from "../../../data/util/duplicate-check.service";
import {
  adminStaffDefaultRole,
  adminStaffDefaultPrivileges,
  demoStaffDefaultRole,
  demoStaffDefaultPrivileges,
} from "../../admin-section-group/section-details/section-details.component";
import { CreateMemberService } from "../../../data/member/create-member.service";
import { getRedirectUri } from '../../../utils';
import { CreateSectionGroupService } from "../../../data/section-group/create-section-group.service";
import { CreateSectionService } from "../../../data/section/create-section.service";
import { CreateBelongsService } from "../../../data/belongs/create-belongs.service";
import { CreateSectionGroupAdminService } from "../../../data/section-group/create-section-group-admin.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { ConfirmDialogComponent } from "../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { CREATE_SECTION_GROUP_AND_SECTION_I18N } from "./create-section-group-and-section.i18n";

// セクショングループ情報（Step 1）は共通のサブフォームを使う
import { SectionGroupFormComponent } from "../../shared/section-group-detail-form/section-group-detail-form.component";
import {
  Mode,
  SectionGroupDocument,
  createDefaultSectionGroupData,
} from "../../shared/section-group-detail-form/section-group-detail.models";

// セクション情報（Step 2）も、Section詳細ダイアログと同じ共通サブフォームを使う。
// 住所/連絡先を AddressesFormComponent / ContactDetailsFormComponent に切り出す作業や、
// 郵便番号の自動検索(ZipLookup)を共通コンポーネント側へ持たせる作業は別途行う予定なので、
// 現時点では SectionDetailsFormComponent が持っている独自の住所・連絡先フィールドをそのまま使う。
import { SectionDetailsFormComponent } from "../../shared/section-details-form/section-details-form.component";

// 「自分自身を管理者として登録する」「新規に管理者を追加する」（Step 3）も共通のサブフォームを使う
import {
  SectionGroupAdminAssignmentComponent,
  SectionGroupAdminAssignmentJob,
  buildSelfAssignJob,
  buildNewAdminJob,
} from "../../shared/section-group-admin-assignment/section-group-admin-assignment.component";

@Component({
  selector: "app-create-section-group-and-section",
  templateUrl: "./create-section-group-and-section.component.html",
  styleUrls: ["./create-section-group-and-section.component.scss"],
  imports: [
    SHARED_IMPORTS,
    SectionGroupFormComponent,
    SectionDetailsFormComponent,
    SectionGroupAdminAssignmentComponent,
  ],
})
export class CreateSectionGroupAndSection implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly msalService = inject(MsalService);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = CREATE_SECTION_GROUP_AND_SECTION_I18N;

  @ViewChild("submitButton") submitButton: HTMLButtonElement;

  /**
   * 実際にスクロールしているのは window ではなく .contents__section
   * （scss で overflow-y: auto が設定されているのはこの要素）。
   * ステップ切り替え時はここをスクロールリセットする必要がある。
   */
  @ViewChild("contentsSection") contentsSectionEl?: ElementRef<HTMLElement>;

  /** Step 1 のセクショングループ情報サブフォーム。validate() / buildPostBody() / enableInput() を呼ぶために参照する */
  @ViewChild("sgForm") sectionGroupForm!: SectionGroupFormComponent;

  /** Step 2 のセクション情報サブフォーム。enableInput() を呼ぶために参照する */
  @ViewChild("sectionDetailsForm") sectionDetailsForm!: SectionDetailsFormComponent;

  /** Step 3 の「自分自身を管理者として登録する」「新規に管理者を追加する」サブフォーム */
  @ViewChild("adminAssignmentComp") adminAssignmentComp?: SectionGroupAdminAssignmentComponent;

  /** カスタムステッパーの現在のステップインデックス */
  readonly currentStepIndex = signal(0);
  isSending = signal(false);

  matcher = new MyErrorStateMatcher();

  sectionFormGroup: FormGroup;
  sectionGroupFormGroup: FormGroup;

  /**
   * 「自分自身を管理者として登録する」「新規に管理者を追加する」用のサブフォーム。
   * sectionGroupFormGroup / sectionFormGroup とはあえて別インスタンスにしている
   * （SectionGroupDetailsComponent と同じ考え方。他画面での使い回しをしやすくするため）。
   */
  adminAssignmentFormGroup: FormGroup;

  /**
   * Step 3 に「自分自身をセクションの管理者にする」「新規に管理者を追加する」の中の
   * 「セクショングループの管理者として登録」「セクションの管理者として登録」を追加するための
   * サブフォーム。SectionGroupAdminAssignmentComponent はダイアログ（section-group-details）とも
   * 共有しているため、Section固有のこれらの項目はコンポーネント自体には持たせず、
   * ウィザード側でこの独立したFormGroupとして持ち、実行時に named ng-content で差し込む。
   */
  sectionAdminExtrasFormGroup: FormGroup;

  /** サブフォームに渡す。テンプレート一覧は HTTP で後から届く（届くと子側が絞り込みをやり直す） */
  userDataTemplates: any[] = [];

  /** サブフォームの初期値（新規作成なので既定値） */
  sectionGroupData: SectionGroupDocument = createDefaultSectionGroupData();

  Mode = Mode;

  signInNameDuplicate;
  sectionNameDuplicates;
  sectionGroupNameDuplicates;

  constructor(
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    public snackBar: MatSnackBar,
    public common: DatabaseCacheService,
    private fb: FormBuilder,
    private router: Router,
    private userDataTemplateListComponent: UserDataTemplateListComponent,
    private getParameterService: GetParameterService,
    private duplicateCheckService: DuplicateCheckService,
    private createSectionGroupService: CreateSectionGroupService,
    private createSectionService: CreateSectionService,
    private createBelongsService: CreateBelongsService,
    private createSectionGroupAdminService: CreateSectionGroupAdminService,
    private createMemberService: CreateMemberService,
  ) {}

  ngOnInit(): void {
    this.prepareFormGroups();
    this.getUserDataTemplates();

    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noData) => {});
  }

  ngAfterViewInit(): void {
    // 子（請求情報サブフォームなど）の ViewChild が初期化されたあとでないと期待どおり動かないため、ここで行う。
    // 項目ごとの有効化は、子が権限（hasAllSectionGroupManagement など）を見て決める。
    this.sectionGroupForm.enableInput();
    this.sectionDetailsForm.enableInput();

    // 権限に応じて有効化するかどうかは app-section-group-admin-assignment 自身が判断する
    this.adminAssignmentComp?.enableInput();

    // sectionAdminExtrasFormGroup は app-section-group-admin-assignment に差し込む
    // 独自の追加項目なので、有効化の判断もそれと同じ権限（hasAllSectionGroupManagement）で揃える。
    if (this.common.hasAllSectionGroupManagement()) {
      this.sectionAdminExtrasFormGroup.enable();
    }
  }

  private prepareFormGroups() {
    // セクショングループのフォームは共通サブフォームの定義を使う
    this.sectionGroupFormGroup = SectionGroupFormComponent.createSectionGroupGroup(
      this.common.SectionGroupFunctions.map((f) => f.code),
    );
    this.sectionGroupFormGroup.disable();

    // 代理店のエージェントであれば契約チャネルを代理店に強制する。
    // section-group-details.component.ts（ダイアログ）と同じロジックのため、
    // SectionGroupFormComponent.applyDistributorAgentDefaults() に一本化している。
    // patchValue より前にやっておく必要がある。
    SectionGroupFormComponent.applyDistributorAgentDefaults(this.common, this.sectionGroupData);

    this.sectionGroupFormGroup.patchValue(this.sectionGroupData);

    // セクションのフォームも、Section詳細ダイアログと同じ共通サブフォームの定義を使う。
    // id と numOfLicence は SectionDetailsFormComponent.enableInput() が意図的に有効化しない
    // （常に読み取り専用）ため、他のフォームと同様に先に disable() しておく必要がある。
    // これを忘れると numOfLicence（Validators.required）が有効なまま空文字で残り、
    // 画面には表示されないのにバリデーションエラーで「次へ」に進めなくなる。
    this.sectionFormGroup = SectionDetailsFormComponent.createSectionDetailsGroup();
    this.sectionFormGroup.disable();

    // 「自分自身を管理者として登録する」「新規に管理者を追加する」は完全に独立した FormGroup として持つ
    this.adminAssignmentFormGroup = SectionGroupAdminAssignmentComponent.createSectionGroupAdminAssignmentGroup();
    this.adminAssignmentFormGroup.disable();

    if (this.common.hasAllSectionGroupManagement()) {
      this.adminAssignmentFormGroup.get("assignSelfAsAdmin")?.setValue(false);
    } else {
      // 現在のユーザーが All Section Group Management を持っていないときは現在のユーザーが管理者になる前提
      this.adminAssignmentFormGroup.get("assignSelfAsAdmin")?.setValue(true);
      this.adminAssignmentFormGroup.get("createNewAdmin")?.setValue(false);
    }

    // Step 3 に差し込む追加項目（セクション管理者関連）。デフォルトは
    // 「自分自身をセクションの管理者にする」= オフ、「グループ管理者として登録」「セクション管理者として登録」= 両方オン。
    this.sectionAdminExtrasFormGroup = new FormGroup({
      assignSelfAsSectionAdmin: new FormControl(false),
      registerAsGroupAdmin: new FormControl(true),
      registerAsSectionAdmin: new FormControl(true),
    });
    this.sectionAdminExtrasFormGroup.disable();

    // デフォルト値を記録（クリア時の復元用）
    this.formDefaults.set(this.sectionGroupFormGroup, this.createSectionGroupResetValue());
    this.formDefaults.set(this.sectionFormGroup, { quotaModel: 'SHARED', monthlyQuotaGB: '10' });
    this.formDefaults.set(this.adminAssignmentFormGroup, this.createAdminAssignmentResetValue());
    this.formDefaults.set(this.sectionAdminExtrasFormGroup, {
      assignSelfAsSectionAdmin: false,
      registerAsGroupAdmin: true,
      registerAsSectionAdmin: true,
    });
  }

  /** セクショングループのフォームをクリアするときに戻す値（既定値 + 機能チェックはすべてオフ） */
  private createSectionGroupResetValue(): Record<string, unknown> {
    const defaults = createDefaultSectionGroupData();

    // クリア後も、代理店のエージェントであれば契約チャネルを代理店に強制した状態に戻す
    SectionGroupFormComponent.applyDistributorAgentDefaults(this.common, defaults);

    const functionAttributes: Record<string, boolean> = {};
    this.common.SectionGroupFunctions.forEach((f) => (functionAttributes[f.code] = false));

    return {
      ...defaults,
      allFunctions: false,
      settings: { ...defaults.settings, functionAttributes },
    };
  }

  /** 管理者割り当てのフォームをクリアするときに戻す値（権限に応じた既定チェック状態） */
  private createAdminAssignmentResetValue(): Record<string, unknown> {
    const hasAllSectionGroupManagement = this.common.hasAllSectionGroupManagement();
    return {
      assignSelfAsAdmin: !hasAllSectionGroupManagement,
      createNewAdmin: false,
      passwordGenerateOnServer: true,
    };
  }

  async createAll() {
    let conflict = await this.doesNameConflict();
    if (conflict) {
      return;
    }

    // セクショングループ情報の検証と送信用データの組み立ては、サブフォームに任せる
    const sectionGroupBody = this.sectionGroupForm.buildPostBody();
    if (sectionGroupBody.status === "error") {
      this.snackBar.open(sectionGroupBody.message, this.i18n.button.close, { duration: 30000 });
      this.currentStepIndex.set(0);
      return;
    }

    this.isSending.set(true);
    try {
      let sectionGroup = await this.createSectionGroup(sectionGroupBody.value);
      if (sectionGroup == null) {
        return;
      }
      let sectionGroupId = sectionGroup.id;

      const user = this.msalService.instance.getActiveAccount();
      const loginUserId = user.idTokenClaims["sub"];

      // 「自分自身を管理者として登録する」（セクショングループ）と
      // 「自分自身をセクションの管理者にする」は、それぞれ独立したチェックボックスなので個別に判定する
      // （以前は selfJob がチェックされていれば自動的に Staff 登録も行っていたが、
      // 　この2つを別のチェックボックスに分けたことで、それぞれ単独でオン/オフできるようにした）。
      //
      // assignSelfAsAdmin の初期値は prepareFormGroups()（ngOnInit）の時点で
      // common.hasAllSectionGroupManagement() を見て一度だけ決めている。しかし、このページを
      // 直接開いた（リロード・直接URLアクセスなど）場合、ngOnInit実行時点では権限データの
      // 取得がまだ完了していないことがあり、そのタイミングで決めた値と、実際に画面に表示される
      // （canManageAdminAssignment() を都度評価する）権限表示がズレてしまうことがある。
      // そのズレが起きると「あなた自身が管理者として登録されます」という固定メッセージが
      // 表示されているのに、assignSelfAsAdmin の値は古い（false の）ままで、自分自身を
      // 管理者登録するAPIが呼ばれない、という不具合につながる。
      // 送信時点では、チェックボックスの値を鵜呑みにせず、
      // canManageAdminAssignment() の最新の判定結果を正とする。
      // 権限が無い（canManageAdminAssignment() === false）ユーザーは常に自分自身が
      // 管理者として登録される前提（UI上もチェックボックスを操作できない）なので、
      // そのケースでは強制的に自分自身を登録するジョブを組み立てる。
      const canManageAdminAssignment = this.adminAssignmentComp?.canManageAdminAssignment()
        ?? this.common.hasAllSectionGroupManagement();
      const selfGroupJob = canManageAdminAssignment
        ? buildSelfAssignJob(this.adminAssignmentFormGroup, loginUserId)
        : {
            createUser: false,
            passwordGenerateOnServer: false,
            sendNotificationEmail: true,
            userId: loginUserId,
          };

      // セクショングループ作成直後、セクション作成の前に自分自身をセクショングループ管理者として
      // 登録する。セクション作成APIは「そのセクショングループに対する権限を持つユーザー」でなければ
      // 呼べないため、assignSelfAsAdmin が必要なケース（権限が無いユーザー、または権限があって
      // チェックをオンにしたユーザー）では、ここで先に管理者登録を済ませておかないと、
      // 直後のセクション作成が権限不足で失敗する
      // （以前は セクショングループ作成 → セクション作成 → 自分自身を管理者登録 の順だったため、
      // 新規に作成したセクショングループに対する権限がまだ無い状態でセクション作成を呼んでしまい、
      // 失敗していた）。
      if (selfGroupJob) {
        const selfAdmin = await this.createSectionGroupAdminJob(sectionGroupId, selfGroupJob);
        if (selfAdmin == null) {
          return;
        }
      }

      let section = await this.createSection(sectionGroupId);
      if (section == null) {
        return;
      }
      let sectionId = section.sectionId;

      if (this.sectionAdminExtrasFormGroup.get("assignSelfAsSectionAdmin")?.value) {
        const selfStaff = await this.createStaff(sectionId, loginUserId);
        if (selfStaff == null) {
          return;
        }
      }

      // 「新規に管理者を追加する」がオンのとき、その新規管理者を
      // 「セクショングループの管理者として登録」「セクションの管理者として登録」の
      // チェック状態に応じてどちらか一方または両方に登録する。
      const newAdminJob = buildNewAdminJob(this.adminAssignmentFormGroup);
      if (newAdminJob) {
        const registerAsGroupAdmin = this.sectionAdminExtrasFormGroup.get("registerAsGroupAdmin")?.value;
        const registerAsSectionAdmin = this.sectionAdminExtrasFormGroup.get("registerAsSectionAdmin")?.value;

        if (registerAsGroupAdmin) {
          // グループ管理者としてユーザーを新規作成する（createUser=true はこの呼び出しだけが行う）
          const newAdmin = await this.createSectionGroupAdminJob(sectionGroupId, newAdminJob);
          if (newAdmin == null) {
            return;
          }

          if (registerAsSectionAdmin) {
            // 両方オンのときは、いま作成したユーザーをそのままセクションのStaffとしても登録する
            // （ユーザー作成は1回だけで済ませる）
            const newStaff = await this.createStaff(sectionId, newAdmin.userId);
            if (newStaff == null) {
              return;
            }
          }
        } else if (registerAsSectionAdmin) {
          // グループ管理者としては登録せず、セクションの管理者としてのみ新規ユーザーを作成する。
          // createBelongs（createStaff）は既存ユーザー専用で createUser を持たないため、
          // このケースだけは createMemberService.createMember() で
          // 新規ユーザー作成とセクションへの登録を1回のAPI呼び出しで行う。
          const newMember = await this.createSectionMember(sectionId, newAdminJob);
          if (newMember == null) {
            return;
          }
        }
        // registerAsGroupAdmin / registerAsSectionAdmin が両方falseの状態は
        // onRegisterAsGroupAdminChange() / onRegisterAsSectionAdminChange() がUI操作の時点で防いでいるため、
        // ここには到達しない想定。
      }

      this.snackBar.open(
        this.i18n.message.createSuccess,
        this.i18n.button.close,
        { duration: 3000 },
      );

      this.router.navigate(["/system-admin/", "section-groups"]);
    } finally {
      this.isSending.set(false);
    }
  }

  /**
   * 「グループ管理者として登録」「セクション管理者として登録」は、少なくともどちらか一方は
   * オンでなければならない（新規に管理者を追加するのに、どちらの管理者としても登録しない、
   * という状態を許さないため）。ユーザーが最後の1つをオフにしようとしたら、その場で戻す。
   */
  onRegisterAsGroupAdminChange(event: MatCheckboxChange): void {
    if (!event.checked && !this.sectionAdminExtrasFormGroup.get("registerAsSectionAdmin")?.value) {
      this.sectionAdminExtrasFormGroup.get("registerAsSectionAdmin")?.setValue(true, { emitEvent: false });
    }
  }

  /** onRegisterAsGroupAdminChange() の逆側。同じ理由で、最後の1つをオフにはさせない。 */
  onRegisterAsSectionAdminChange(event: MatCheckboxChange): void {
    if (!event.checked && !this.sectionAdminExtrasFormGroup.get("registerAsGroupAdmin")?.value) {
      this.sectionAdminExtrasFormGroup.get("registerAsGroupAdmin")?.setValue(true, { emitEvent: false });
    }
  }

  async doesNameConflict() {
    const willCreateNewAdmin = this.adminAssignmentFormGroup.get("createNewAdmin")?.value;
    // 新規に管理者を作成しないときは signInName を使わないので、重複チェックの対象からも外す。
    // 空文字 "" ではなく null を渡す（DuplicateCheckService 側でクエリパラメータ自体を
    // 付けないようにするため。"" のままだと signInNames=（値なし）としてサーバーに送られてしまい、
    // サーバー側は null 判定を通過して空文字での重複チェックを実行し InternalServerError になっていた）。
    let signInName = willCreateNewAdmin ? this.adminAssignmentFormGroup.get("signInName")?.value : null;
    let sectionGroupName = this.sectionGroupFormGroup.get("name")?.value ?? null;
    let sectionName = this.sectionFormGroup.get("sectionName")?.value ?? null;
    try {
      const res = await this.duplicateCheckService
        .DuplicateCheck(signInName, sectionGroupName, sectionName)
        .toPromise();
      let duplicates = res as any[];

      if (duplicates.some((d) => "SectionGroupName" in d)) {
        this.snackBar.open(
          `${this.i18n.duplicate.sectionGroupName}${sectionGroupName}${this.i18n.duplicate.alreadyUsed}`,
          this.i18n.button.close,
          {
            duration: 3000,
          },
        );
        this.currentStepIndex.set(0);
        return true;
      }

      if (duplicates.some((d) => "SectionName" in d)) {
        this.snackBar.open(
          `${this.i18n.duplicate.sectionName}${sectionName}${this.i18n.duplicate.alreadyUsed}`,
          this.i18n.button.close,
          {
            duration: 3000,
          },
        );
        this.currentStepIndex.set(1);
        return true;
      }

      if (willCreateNewAdmin && duplicates.some((d) => "SignInName" in d)) {
        this.snackBar.open(
          `${this.i18n.duplicate.signInName}${signInName}${this.i18n.duplicate.alreadyUsed}`,
          this.i18n.button.close,
          {
            duration: 3000,
          },
        );
        this.currentStepIndex.set(2);
        return true;
      }
    } catch {
      return true;
    }

    return false;
  }

  getUserDataTemplates() {
    this.getParameterService
      .getParameter(this.userDataTemplateListComponent.parameterName)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.userDataTemplates = (res as any).parameterValue;
        },
        (error) => {
          // Error handling
        },
      );
  }

  /** 指定ステップに移動（バリデーション付き） */
  goToStep(index: number): void {
    // 前に戻る場合はバリデーション不要
    if (index < this.currentStepIndex()) {
      this.setStep(index);
      return;
    }

    // 前方に進む場合は各ステップをバリデーション
    if (index > 0) {
      // セクショングループ情報の検証はサブフォームに任せる（メッセージも子が返す）
      const message = this.sectionGroupForm.validate();
      if (message) {
        this.snackBar.open(message, this.i18n.button.close, { duration: 3000 });
        this.setStep(0);
        return;
      }
    }

    if (index > 1) {
      this.sectionFormGroup.markAllAsTouched();
      this.sectionFormGroup.updateValueAndValidity();
      // disabled な FormGroup は status が 'DISABLED' になり、valid（status === 'VALID'）は
      // false を返してしまう。「本当に無効な項目があるか」だけを見たいので invalid で判定する
      // （invalid は status === 'INVALID' のときだけ true）。
      if (this.sectionFormGroup.invalid) {
        this.snackBar.open(this.i18n.validation.checkFields, this.i18n.button.close, { duration: 3000 });
        this.setStep(1);
        return;
      }
    }

    if (index > 2) {
      this.adminAssignmentFormGroup.markAllAsTouched();
      this.adminAssignmentFormGroup.updateValueAndValidity();
      // 権限が無いユーザーは SectionGroupAdminAssignmentComponent.enableInput() が
      // 何も enable() しないため、adminAssignmentFormGroup が丸ごと disabled のままになる。
      // その場合 status は 'DISABLED' になり valid は false を返すので、
      // ここも invalid（status === 'INVALID'）で判定する必要がある。
      if (this.adminAssignmentFormGroup.invalid) {
        this.snackBar.open(this.i18n.validation.checkFields, this.i18n.button.close, { duration: 3000 });
        this.setStep(2);
        return;
      }
    }

    this.setStep(index);
  }

  /**
   * currentStepIndex を更新し、あわせてスクロール位置を一番上に戻す。
   * 各ステップは [hidden] で切り替えているだけで高さが違うため、切り替えずに
   * スクロール位置をそのままにしておくと、遷移後にページの中ほどが開いた状態になってしまう。
   *
   * 実際にスクロールしているのは window ではなく .contents__section（scss で
   * overflow-y: auto が設定されているのはこの要素）なので、window.scrollTo() は効かない。
   * .contents__section をスクロールリセットする（念のため window 側もリセットしておく）。
   */
  private setStep(index: number): void {
    this.currentStepIndex.set(index);
    this.contentsSectionEl?.nativeElement.scrollTo({ top: 0, behavior: "auto" });
    window.scrollTo({ top: 0, behavior: "auto" });
  }

  /** 次のステップへ */
  nextStep(): void {
    this.goToStep(this.currentStepIndex() + 1);
  }

  /** 前のステップへ */
  prevStep(): void {
    this.goToStep(this.currentStepIndex() - 1);
  }

  /**
   * 確認ダイアログ付きフォームクリア。
   * Step 3 は adminAssignmentFormGroup と sectionAdminExtrasFormGroup の2つの独立した
   * FormGroup にまたがっているため、複数渡せるようにしている（確認ダイアログは1回だけ出す）。
   */
  clearForm(formGroup: FormGroup, ...moreFormGroups: FormGroup[]): void {
    const formGroups = [formGroup, ...moreFormGroups];
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.i18n.dialog.clearTitle,
        message: this.i18n.dialog.clearMessage,
        confirmButtonText: this.i18n.dialog.clearConfirm,
        variant: 'danger',
      },
    });
    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) {
        return;
      }

      formGroups.forEach((fg) => {
        const defaults = this.formDefaults.get(fg);
        fg.reset(defaults);
      });

      // サブフォームは (input) などのイベントで表示を更新しているため、reset 後は手動で更新する
      if (formGroups.includes(this.sectionGroupFormGroup)) {
        this.sectionGroupForm.refreshPreviewAndErrors();
        this.sectionGroupForm.refreshAllFunctionsCheckbox();
      }
    });
  }

  /** フォームごとのデフォルト値マップ */
  private formDefaults = new Map<FormGroup, Record<string, unknown>>();

  /** セクショングループの住所・連絡先をセクションのフォームにコピーする（グループ側は入れ子構造） */
  copyAddressFromGroupToSection() {
    ["zip", "prefecture", "city", "town", "line1", "line2"].forEach((field) => {
      this.sectionFormGroup
        .get(field)
        ?.setValue(this.sectionGroupFormGroup.get(`addresses.${field}`)?.value);
    });

    ["phone", "mobile", "email"].forEach((field) => {
      this.sectionFormGroup
        .get(field)
        ?.setValue(this.sectionGroupFormGroup.get(`contactDetails.${field}`)?.value);
    });
  }

  /** @param postParams サブフォームの buildPostBody() が組み立てた送信用データ */
  async createSectionGroup(postParams: any) {
    postParams.sectionGroupId = this.common.getSelectedSectionGroup()?.id;

    try {
      const res = await this.createSectionGroupService
        .createSectionGroup(postParams)
        .toPromise();
      let newSectonGroup = res as any;

      return newSectonGroup;
    } catch (error) {
      this.snackBar.open(
        `${this.i18n.message.sectionGroupCreateError}${error.error.message}`,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );

      return null;
    }
  }

  async createSection(sectionGroupId) {
    let postParams = this.getPostParamsSection(sectionGroupId);

    try {
      const res = await this.createSectionService
        .createSection(sectionGroupId, postParams)
        .toPromise();
      let newSecton = res as any;

      return newSecton;
    } catch (error) {
      this.snackBar.open(
        `${this.i18n.message.sectionCreateError}${error.error.message}`,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );

      return null;
    }
  }

  getPostParamsSection(sectionGroupId) {
    var postParams: Record<string, any> = {};

    // .value ではなく getRawValue() を使う。.value は disabled な子コントロールを
    // 結果から丸ごと除外してしまうため（quotaModel / monthlyQuotaGB は
    // hasQuotaManagement() を持たないユーザーには enableInput() で有効化されず
    // disabled のまま）、.value だと settings が空オブジェクトのまま送られてしまい、
    // サーバー側のバリデーションで弾かれていた（InternalServerError の原因）。
    const raw = this.sectionFormGroup.getRawValue();

    postParams.sectionGroupId = sectionGroupId;
    postParams.sectionName = raw.sectionName;
    postParams.description = raw.description;

    postParams.addresses = {};
    postParams.addresses.zip = raw.zip;
    postParams.addresses.prefecture = raw.prefecture;
    postParams.addresses.city = raw.city;
    postParams.addresses.town = raw.town;
    postParams.addresses.line1 = raw.line1;
    postParams.addresses.line2 = raw.line2;

    postParams.contactDetails = {};
    postParams.contactDetails.phone = raw.phone;
    postParams.contactDetails.mobile = raw.mobile;
    postParams.contactDetails.email = raw.email;

    postParams.contractInfo = {};
    // フォームのコントロール名は contractStatus（status ではない）。
    // 単体の Section 作成ページが送っている正しいペイロードでは
    // contractInfo.contractStatus になっており、名前が食い違っていた。
    postParams.contractInfo.contractStatus = raw.contractStatus;

    postParams.settings = {};
    postParams.settings.quotaModel = raw.quotaModel;
    postParams.settings.monthlyQuotaGB = raw.monthlyQuotaGB;

    return postParams;
  }

  /**
   * SectionGroupAdminCreate を1回呼び出す。
   * 「自分自身を管理者として登録する」「新規に管理者を追加する」のどちらも、
   * SectionGroupAdminAssignmentComponent が組み立てる job（0〜2件）をそのまま渡せば済むよう、
   * 共通の呼び出し処理としてここにまとめている。
   */
  async createSectionGroupAdminJob(sectionGroupId: string, job: SectionGroupAdminAssignmentJob) {
    const roles = this.common.sectionGroupAdminRoles.map((r) => r.code);
    const postParams: Record<string, any> = {
      ...job,
      sectionGroupId,
      roles,
    };

    try {
      const res = await this.createSectionGroupAdminService
        .createSectionGroupAdmin(sectionGroupId, postParams)
        .toPromise();
      return res as any;
    } catch (error: any) {
      this.snackBar.open(
        `${this.i18n.message.sectionGroupAdminCreateError}${error?.error?.message ?? JSON.stringify(error)}`,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );

      return null;
    }
  }

  async createStaff(sectionId, userId) {
    let postParams = this.getPostParamsStaff(sectionId, userId);

    try {
      const res = await this.createBelongsService
        .createBelongs(sectionId, postParams)
        .toPromise();
      let newStaff = res as any;

      return newStaff;
    } catch (error) {
      this.snackBar.open(
        `${this.i18n.message.staffCreateError}${error?.error?.message ?? JSON.stringify(error)}`,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );

      return null;
    }
  }

  /**
   * 「グループ管理者として登録」をオフのまま「セクションの管理者として登録」だけをオンにしたときに使う。
   * createBelongs（createStaff）は既存ユーザー登録専用（createUser を持たない）ため、
   * このケースだけは createMemberService.createMember() で新規ユーザーの作成と
   * セクションへの登録を1回のAPI呼び出しで行う
   * （admin-section-group/section-details.component.ts の新規Staff作成ロジックを踏襲）。
   *
   * ※ passwordGenerateOnServer / パスワードは、元のロジックのように常時サーバー生成に固定するのではなく、
   *   adminAssignmentFormGroup の「パスワードをサーバー側で生成する」の選択をそのまま尊重している
   *   （buildNewAdminJob() と挙動を揃えるため）。
   * ※ staffUserManageRole / staffAccountManageRole に相当するチェックボックスはこの画面には無いため、
   *   role / privileges は demoStaffDefaultRole / demoStaffDefaultPrivileges のまま追加なしで送る。
   */
  async createSectionMember(sectionId: string, newAdminJob: SectionGroupAdminAssignmentJob) {
    const postParams: Record<string, any> = {
      createUser: true,

      staffSignInName: newAdminJob.signInNames,
      passwordGenerateOnServer: newAdminJob.passwordGenerateOnServer,
      staffPassword: newAdminJob.passwordGenerateOnServer ? undefined : newAdminJob.loginPassword,
      staffFirstName: newAdminJob.firstName,
      staffLastName: newAdminJob.lastName,
      staffEmailAddress: newAdminJob.email,
      staffBirthDate: newAdminJob.birthDate,
      staffSex: newAdminJob.sex,

      sendNotificationEmail: true,
      notificationEmailSubject: "GOKURI: 利用開始の準備ができました",
      siteTopUrl: getRedirectUri(),

      sectionId: sectionId,
      role: [...demoStaffDefaultRole],
      privileges: [...demoStaffDefaultPrivileges],
      userType: "STAFF",
      jobTitleCode: "OTHER",
    };

    try {
      const res = await this.createMemberService.createMember(postParams, sectionId).toPromise();
      return res as any;
    } catch (error: any) {
      this.snackBar.open(
        `${this.i18n.message.staffCreateError}${error?.error?.message ?? JSON.stringify(error)}`,
        this.i18n.button.close,
        {
          duration: 30000,
        },
      );

      return null;
    }
  }

  getPostParamsStaff(sectionId, userId) {
    var postParams: Record<string, any> = {};

    postParams.userId = userId;
    postParams.sectionId = sectionId;
    postParams.role = adminStaffDefaultRole;
    postParams.privileges = adminStaffDefaultPrivileges;
    postParams.userType = "STAFF";
    postParams.jobTitleCode = "OTHER";

    return postParams;
  }
}

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}
